import React, { useEffect, useState } from 'react';
import { ArrowLeft, CheckCircle, Save } from 'lucide-react';

const COLORS = {
  navyBlue: '#003366',
  orange: '#FF6B35',
  mustard: '#FFAA00',
  green: '#16A34A',
  lightGray: '#F9FAFB',
  white: '#FFFFFF',
  borderGray: '#D1D5DB',
  textGray: '#6B7280',
  textDark: '#1F2937',
};

const STYLES = [
  { key: 'visual', label: 'Visual', emoji: '👁️', bg: '#FFF7ED', border: '#FDBA74', iconBg: '#FFEDD5' },
  { key: 'auditory', label: 'Auditory', emoji: '👂', bg: '#F0F9FF', border: '#BAE6FD', iconBg: '#E0F2FE' },
  { key: 'kinesthetic', label: 'Kinesthetic', emoji: '🤚', bg: '#F0FDF4', border: '#86EFAC', iconBg: '#DCFCE7' },
];

const fetchWithTimeout = (url, options = {}, timeoutMs = 30000) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const token = window.sessionStorage.getItem('nwaAccessToken');
  const headers = { ...(options.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;
  return fetch(url, { ...options, headers, signal: controller.signal }).finally(() => clearTimeout(timer));
};

const todayDDMMYYYY = () => {
  const d = new Date();
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
};

const styles = {
  container: { minHeight: '100vh', backgroundColor: COLORS.lightGray },
  header: { backgroundColor: COLORS.navyBlue, color: COLORS.white, padding: '8px 16px', borderBottom: `2px solid ${COLORS.mustard}` },
  headerContent: { maxWidth: '900px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  backButton: { backgroundColor: 'transparent', border: 'none', color: COLORS.white, display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', padding: '8px 0' },
  body: { maxWidth: '900px', margin: '0 auto', padding: '24px' },
  card: { backgroundColor: COLORS.white, borderRadius: '8px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', padding: '24px', marginBottom: '20px' },
  label: { display: 'block', fontSize: '14px', fontWeight: '600', color: COLORS.textDark, marginBottom: '6px' },
  input: { width: '100%', padding: '10px 14px', border: `2px solid ${COLORS.borderGray}`, borderRadius: '6px', fontSize: '15px', boxSizing: 'border-box', marginBottom: '16px' },
  select: { width: '100%', padding: '10px 14px', border: `2px solid ${COLORS.borderGray}`, borderRadius: '6px', fontSize: '15px', backgroundColor: COLORS.white, boxSizing: 'border-box', marginBottom: '16px' },
  button: { backgroundColor: COLORS.orange, color: COLORS.white, padding: '12px 24px', borderRadius: '6px', fontWeight: '600', border: 'none', cursor: 'pointer', fontSize: '16px' },
  errorBox: { backgroundColor: '#FEE2E2', border: '1px solid #FCA5A5', color: '#991B1B', padding: '12px 16px', borderRadius: '6px', marginBottom: '16px' },
};

export default function LearningStyleScreening({ student, apiUrl, facilitatorId, facilitatorName, schoolId, schoolName, programmeId, programmeName, initialTimepoint, correctionMode = false, onBack, onSaved }) {
  const [stage, setStage] = useState('setup');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [screeningDate, setScreeningDate] = useState(todayDDMMYYYY());
  const timepoint = initialTimepoint || 'Baseline';
  const [administrationMode, setAdministrationMode] = useState('Individual');
  const administrationBatchId = ''; // kept blank internally; no practitioner entry needed
  const [assessorName, setAssessorName] = useState(facilitatorName || '');
  const [notes, setNotes] = useState('');
  const [scores, setScores] = useState({ visual: '', auditory: '', kinesthetic: '' });
  const [result, setResult] = useState(null);
  const [correctionLoading, setCorrectionLoading] = useState(false);
  const [correctionLoaded, setCorrectionLoaded] = useState(!correctionMode);

  useEffect(() => {
    if (!correctionMode || !student?.student_code) return;
    let cancelled = false;
    setCorrectionLoading(true);
    setCorrectionLoaded(false);
    setError('');
    fetchWithTimeout(`${apiUrl}/api/learning-style/record/${student.student_code}?timepoint=${encodeURIComponent(timepoint)}`)
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Could not load the saved Learning Style record');
        return data;
      })
      .then((data) => {
        if (cancelled) return;
        const savedScores = data.scores || {};
        if ([savedScores.visual, savedScores.auditory, savedScores.kinesthetic].some((value) => value === '' || value === null || value === undefined)) {
          throw new Error('The saved Learning Style scores could not be loaded completely');
        }
        setScores({
          visual: String(savedScores.visual),
          auditory: String(savedScores.auditory),
          kinesthetic: String(savedScores.kinesthetic),
        });
        if (data.screening_date) setScreeningDate(data.screening_date);
        setNotes(data.notes || '');
        if (data.assessor_name) setAssessorName(data.assessor_name);
        if (data.administration_mode) setAdministrationMode(data.administration_mode);
        setCorrectionLoaded(true);
      })
      .catch((e) => {
        if (!cancelled) {
          setCorrectionLoaded(false);
          setError(e.message || 'Could not load the saved Learning Style record');
        }
      })
      .finally(() => { if (!cancelled) setCorrectionLoading(false); });
    return () => { cancelled = true; };
  }, [correctionMode, student?.student_code, apiUrl, timepoint]);

  const setScore = (key, value) => {
    if (value === '' || (Number.isInteger(Number(value)) && Number(value) >= 0 && Number(value) <= 12)) {
      setScores((prev) => ({ ...prev, [key]: value }));
    }
  };

  const scoreTotal = STYLES.reduce((sum, { key }) => sum + (scores[key] === '' ? 0 : Number(scores[key])), 0);
  const allScored = STYLES.every(({ key }) => scores[key] !== '' && Number(scores[key]) >= 0 && Number(scores[key]) <= 12) && scoreTotal === 12;
  const scorePercent = (key) => scores[key] === '' ? 0 : Math.round((Number(scores[key]) / 12) * 1000) / 10;

  const submit = async () => {
    if (!allScored) {
      setError('Enter the Visual, Auditory and Kinesthetic totals for the 12 administered items. The three scores must add to 12.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const res = await fetchWithTimeout(`${apiUrl}/api/learning-style/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          student_code: student.student_code,
          screening_date: screeningDate,
          timepoint,
          school_id: schoolId || student?.school_id || '',
          school_name: schoolName || '',
          programme_id: programmeId || student?.programme_id || '',
          programme_name: programmeName || '',
          assessor_name: assessorName,
          administration_mode: administrationMode,
          administration_batch_id: administrationBatchId,
          entered_by_user_id: facilitatorId,
          entered_by_name: facilitatorName,
          notes,
          correction_mode: correctionMode,
          scores: {
            visual: Number(scores.visual),
            auditory: Number(scores.auditory),
            kinesthetic: Number(scores.kinesthetic),
          },
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not save this Learning Style administration');
      setResult(data);
      setStage('complete');
      if (onSaved) onSaved(data);
    } catch (e) {
      setError(e.message || 'Could not save this Learning Style administration');
    } finally {
      setLoading(false);
    }
  };

  const Header = ({ title }) => (
    <div style={styles.header}>
      <div style={styles.headerContent}>
        <button style={styles.backButton} onClick={onBack}><ArrowLeft size={18} /> Back to {student.full_name}</button>
        <span style={{ fontSize: '13px', color: COLORS.mustard, fontWeight: '600' }}>{title}</span>
      </div>
    </div>
  );

  if (stage === 'complete') {
    return (
      <div style={styles.container}>
        <Header title="Learning Style: Saved" />
        <div style={styles.body}>
          <div style={styles.card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: COLORS.green, marginBottom: '16px' }}>
              <CheckCircle size={28} />
              <h2 style={{ fontSize: '20px', fontWeight: 'bold', margin: 0 }}>Saved</h2>
            </div>

            {result?.profile && (
              <p style={{ color: COLORS.textDark, marginBottom: '20px' }}><strong>Scores:</strong> {result.profile}</p>
            )}
            <button style={{ ...styles.button, backgroundColor: COLORS.white, color: COLORS.navyBlue, border: `2px solid ${COLORS.navyBlue}` }} onClick={onBack}>
              Back to {student.full_name}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <Header title="Learning Style" />
      <div style={styles.body}>
        <div style={{ ...styles.card, padding: '18px 20px' }}>
          <h1 style={{ fontSize: '22px', fontWeight: 'bold', color: COLORS.navyBlue, margin: '0 0 6px' }}>
            Learning Style - {student.full_name}
          </h1>
          <p style={{ color: COLORS.textGray, marginTop: 0, marginBottom: '20px' }}>Timepoint: {timepoint}</p>

          {error && <div style={styles.errorBox}>{error}</div>}
          {correctionMode && (
            <div style={{ backgroundColor: '#FFF8EF', border: '1px solid #F2D5AE', color: '#7A5735', padding: '9px 11px', borderRadius: '6px', marginBottom: '14px', fontSize: '13px' }}>
              {correctionLoading
                ? 'Correction mode - loading the previously saved values...'
                : correctionLoaded
                  ? 'Correction mode - previous values loaded. Change only what is needed, then save the correction.'
                  : 'Correction mode - saved values have not loaded. Do not enter new values.'}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '12px', alignItems: 'end' }}>
            <div>
              <label style={styles.label}>Screening date</label>
              <input style={{ ...styles.input, marginBottom: 0 }} value={screeningDate} onChange={(e) => setScreeningDate(e.target.value)} />
            </div>
            <div>
              <label style={styles.label}>Administration</label>
              <select style={{ ...styles.select, marginBottom: 0 }} value={administrationMode} onChange={(e) => setAdministrationMode(e.target.value)}>
                <option value="Individual">Individual (1:1)</option>
                <option value="Group">Group</option>
              </select>
            </div>
            <div>
              <label style={styles.label}>Assessor</label>
              <input style={{ ...styles.input, marginBottom: 0 }} value={assessorName} onChange={(e) => setAssessorName(e.target.value)} />
            </div>
          </div>
        </div>

        <div style={styles.card}>
          <h2 style={{ color: COLORS.navyBlue, fontSize: '18px', marginTop: 0, marginBottom: '4px' }}>Learning Style scores</h2>
          <p style={{ color: COLORS.textGray, fontSize: '13px', marginTop: 0, marginBottom: '14px' }}>12 items administered. Enter how many responses were Visual, Auditory and Kinesthetic. Total: <strong>{scoreTotal}/12</strong>.</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
            {STYLES.map(({ key, label, emoji, bg, border, iconBg }) => (
              <div key={key} style={{ backgroundColor: bg, padding: '14px', borderRadius: '8px', border: `1px solid ${border}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <div style={{ fontSize: '22px', backgroundColor: iconBg, width: '38px', height: '38px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{emoji}</div>
                  <label style={{ fontSize: '15px', fontWeight: '600', color: COLORS.navyBlue }}>{label} *</label>
                </div>
                <input
                  type="number"
                  min="0"
                  max="12"
                  step="1"
                  value={scores[key]}
                  onChange={(e) => setScore(key, e.target.value)}
                  style={{ ...styles.input, fontSize: '15px', padding: '10px', marginBottom: 0 }}
                  placeholder="0 to 12"
                  autoComplete="off"
                />
                <div style={{ marginTop: '8px', fontSize: '13px', fontWeight: '700', color: COLORS.navyBlue }}>{scorePercent(key)}%</div>
              </div>
            ))}
          </div>

          <label style={{ ...styles.label, marginTop: '18px' }}>Recommendations / notes (optional)</label>
          <textarea style={{ ...styles.input, minHeight: '80px', fontFamily: 'inherit' }} value={notes} onChange={(e) => setNotes(e.target.value)} />

          <button style={{ ...styles.button, display: 'flex', alignItems: 'center', gap: '8px' }} onClick={submit} disabled={loading || correctionLoading || (correctionMode && !correctionLoaded) || !allScored}>
            <Save size={18} /> {loading ? 'Saving…' : correctionLoading ? 'Loading saved values…' : correctionMode ? 'Save Correction' : 'Submit & Lock'}
          </button>
        </div>
      </div>
    </div>
  );
}
